'use client'

import { AnimatePresence, motion } from 'framer-motion'
import { useRef, useState } from 'react'
import { useOnClickOutside } from 'usehooks-ts'

import { cn } from '@/lib/utils'

interface Props {
  type: 'spot' | 'perpetual'
  className?: string
  oppositeClassName?: string
  onChange?: (isLong: boolean) => void
  isLong: boolean
  disabled?: boolean
  /** Quick mode: clicking toggles long/short directly without opening dropdown */
  quickMode?: boolean
  /** Tab index for keyboard navigation */
  tabIndex?: number
  /** ClassName for the outer container */
  containerClassName?: string
}

/**
 *
 * @param type - `spot` will show buy/sell selector, `perpetual` will show long/short selector
 * @param isLong - The value of the selector, `true` for long, `false` for short
 * @param quickMode - If true, clicking toggles directly without dropdown
 * @returns
 */
export function BuySellSelector({
  className,
  containerClassName,
  oppositeClassName,
  disabled,
  type,
  isLong,
  onChange,
  quickMode = false,
  tabIndex,
}: Props) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null!)
  const longLabel = type === 'perpetual' ? 'Long' : 'Buy'
  const shortLabel = type === 'perpetual' ? 'Short' : 'Sell'
  useOnClickOutside(rootRef, () => setOpen(false))

  const handleClick = () => {
    if (disabled) return

    if (quickMode) {
      // Quick mode: toggle directly
      onChange?.(!isLong)
    } else {
      // Normal mode: open dropdown
      setOpen(!open)
    }
  }

  return (
    <div className={cn('relative flex min-w-7.5', containerClassName)} ref={rootRef}>
      <button
        type="button"
        data-1p-ignore
        disabled={disabled}
        tabIndex={tabIndex}
        className={cn([
          'group w-full min-w-12 cursor-pointer p-1 text-start text-sm font-semibold tracking-tight underline-offset-2 not-disabled:hover:underline',
          isLong ? 'text-chart-mint' : 'text-chart-red',
          open && 'bg-foreground/20',
          disabled && 'cursor-default',
          className,
        ])}
        onClick={handleClick}
      >
        {isLong ? longLabel : shortLabel}
      </button>
      {/* Dropdown only shown in normal mode (not quick mode) */}
      {!quickMode && (
        <AnimatePresence>
          {open && !disabled && (
            <motion.button
              type="button"
              data-1p-ignore
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.1 }}
              className={cn([
                'absolute top-full left-0 z-50 w-full cursor-pointer border border-white/10 bg-white/5 p-1 text-start text-sm font-semibold tracking-tight shadow-xl not-disabled:hover:underline',
                isLong ? 'text-chart-red' : 'text-chart-mint',
                oppositeClassName,
              ])}
              onClick={() => {
                setOpen(false)
                onChange?.(!isLong)
              }}
            >
              {isLong ? shortLabel : longLabel}
            </motion.button>
          )}
        </AnimatePresence>
      )}
    </div>
  )
}
